import { Sentry, sentryEnabled } from "./lib/sentry";
import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import "./index.css";

/**
 * Global error handler to suppress benign DOM errors caused by browser
 * extensions, translation tools, or password managers that manipulate
 * the DOM outside of React's control.
 */
const BENIGN_ERROR_PATTERNS = [
  "removeChild",
  "insertBefore",
  "appendChild",
  "not a child",
  "n'est pas un enfant",
  "ist kein Kindknoten",
  "ResizeObserver loop",
];

window.addEventListener("error", (event) => {
  const msg = event.message || "";
  if (BENIGN_ERROR_PATTERNS.some((p) => msg.includes(p))) {
    event.preventDefault();
    console.warn("[Suppressed benign DOM error]", msg);
    return;
  }
});

window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason?.message || String(event.reason || "");
  if (BENIGN_ERROR_PATTERNS.some((p) => reason.includes(p))) {
    event.preventDefault();
    console.warn("[Suppressed benign promise rejection]", reason);
    return;
  }
});

const SKIP_SENTRY_TRPC_CODES = new Set([
  "UNAUTHORIZED",
  "NOT_FOUND",
  "BAD_REQUEST",
  "CONFLICT",
]);

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  // Nur Admin-Routen (/admin) nutzen Manus OAuth.
  // Alle anderen Routen (inkl. /app, /login, /) leiten zur Client-Login-Seite.
  const path = window.location.pathname;
  if (path.startsWith("/admin")) {
    window.location.href = getLoginUrl();
  } else {
    window.location.href = "/login";
  }
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
    if (sentryEnabled && error) {
      if (error instanceof TRPCClientError && SKIP_SENTRY_TRPC_CODES.has(error.data?.code)) return;
      Sentry.captureException(error, { tags: { source: "trpc_query" } });
    }
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
    if (sentryEnabled && error) {
      if (error instanceof TRPCClientError && SKIP_SENTRY_TRPC_CODES.has(error.data?.code)) return;
      Sentry.captureException(error, { tags: { source: "trpc_mutation" } });
    }
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);
